import React, { useState } from "react";
import { FaWallet, FaCreditCard, FaUserFriends, FaTimes, FaTag, FaChevronDown, FaChevronUp, FaExchangeAlt } from "react-icons/fa";
import { nairaToBytes, bytesToNaira, formatBytes, formatNaira } from '../utils/currency';
import CurrencyConverterModal from './currency/CurrencyConverterModal';
import { motion, AnimatePresence } from "framer-motion";
import { showErrorNotification, showSuccessNotification } from "../components/NotificationManager";
import EnhancedDiscountSelector from "./EnhancedDiscountSelector";
import { useCart } from "../context/cartContext";

const EnhancedPaymentModal = ({
  isOpen,
  onClose,
  orderData,
  userBalance,
  onCardPayment,
  onFriendPayment,
  loading,
}) => {
  const { clearCart } = useCart();
  const [friendUsername, setFriendUsername] = useState("");
  const [friendMessage, setFriendMessage] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [paymentRequestStatus, setPaymentRequestStatus] = useState(null);
  const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(true);
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);
  const [isFriendPaymentOpen, setIsFriendPaymentOpen] = useState(false);
  const [isWalletLoading, setIsWalletLoading] = useState(false);
  const [isFriendPaymentLoading, setIsFriendPaymentLoading] = useState(false);
  const [showConverter, setShowConverter] = useState(false);

  const BASE_URL = "https://mongobyte-hc73.onrender.com/api/v1";
  const userToken = localStorage.getItem("token");

  // Extract relevant data from orderData with safe defaults
  const totalAmount = orderData?.orderDetails?.totalPrice ?? 0;
  const deliveryFee = orderData?.orderDetails?.fee ?? 0;
  const restaurantId = orderData?.orderDetails?.restaurantCustomId || null;
  const restaurantName =
    orderData?.itemsForRestaurant?.[0]?.meal?.restaurantName ||
    orderData?.orderDetails?.restaurantCustomId ||
    "Unknown Restaurant";
  const isExternal = orderData?.isExternal ?? false;
  const recipientInfo = isExternal
    ? {
      name: orderData?.orderDetails?.recipientName || "External Recipient",
      phone: orderData?.orderDetails?.recipientPhone || "N/A",
      location: orderData?.orderDetails?.location || "N/A",
    }
    : {
      username: orderData?.orderDetails?.orderForUsername || "Self",
      location: orderData?.orderDetails?.location || "N/A",
      phone: orderData?.orderDetails?.phoneNumber || "N/A",
    };

  // Validate discount code
  const validateDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError("Please enter a discount code.");
      return;
    }

    setIsValidatingDiscount(true);
    setDiscountError("");

    try {
      const response = await fetch(`${BASE_URL}/discounts/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          discountCode: discountCode,
          orderAmount: totalAmount - deliveryFee,
          restaurant: restaurantId,
          deliveryFee,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setDiscount(result.preview);
        setDiscountError("");
      } else {
        setDiscountError(result.message);
        setDiscount(null);
      }
    } catch (error) {
      setDiscountError("Failed to validate discount code. Please try again.");
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  // Handle wallet payment
  const handleWalletPayment = async () => {
    if (!orderData?.orderDetails) {
      showErrorNotification("Order details are missing. Please try again.");
      return;
    }

    if (userBalance < (discount ? discount.finalAmount + deliveryFee : totalAmount)) {
      showErrorNotification("Insufficient balance. Please add funds to your wallet or choose another payment method.");
      return;
    }

    setIsWalletLoading(true);
    try {
      const endpoint = isExternal
        ? `${BASE_URL}/orders/gift-external`
        : `${BASE_URL}/orders/create`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...orderData.orderDetails,
          paymentMethod: "wallet",
          discountCode: discount ? discount.discountCode : undefined,
        }),
      });

      const result = await response.json();
      if (result.success) {
        showSuccessNotification(
          `Order placed successfully! Order ID: ${result.order?.customId || "Order"}`
        );
        onClose(true); // Pass true to indicate successful payment
      } else {
        // Check if the message contains "successfully" - backend sometimes sends success messages in error structure
        if (result.message && result.message.toLowerCase().includes("successfully")) {
          showSuccessNotification(result.message);
          onClose(true); // Pass true to indicate successful payment
        } else if (result.message.includes("Insufficient balance")) {
          showErrorNotification(
            "Insufficient balance. Please add funds to your wallet."
          );
        } else if (result.message.includes("not found")) {
          showErrorNotification(
            "Order creation failed: " + result.message
          );
        } else {
          showErrorNotification(result.message || "Failed to place order.");
        }
      }
    } catch (error) {
      console.error("Wallet payment error:", error);
      showErrorNotification("Failed to process wallet payment. Please try again.");
    } finally {
      setIsWalletLoading(false);
    }
  };

  // Handle friend payment
  const handleFriendPayment = async () => {
    if (!friendUsername.trim()) {
      showErrorNotification("Please enter a friend's username.");
      setIsFriendPaymentLoading(false);
      return;
    }

    setIsFriendPaymentLoading(true);
    try {
      // Step 1: Create the order with friend_payment method
      const orderResponse = await fetch(`https://mongobyte-hc73.onrender.com/api/v1/orders/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...orderData.orderDetails,
          paymentMethod: "friend_payment",
          friendPayment: {
            friendUsername: friendUsername.trim(),
            message: friendMessage || `Please help me pay for my food order of ${formatBytes(nairaToBytes(discount ? discount.finalAmount + deliveryFee : totalAmount))} 🙏`,
          },
          discountCode: discount ? discount.discountCode : undefined,
        }),
      });

      const orderResult = await orderResponse.json();
      if (orderResult.paymentPending) {
        showSuccessNotification(
          `Payment request sent to @${friendUsername}! They'll receive a notification to pay for your order.`
        );
        onFriendPayment({
          friendUsername: friendUsername.trim(),
          message: friendMessage,
          orderId: orderResult.order._id,
          paymentRequestId: orderResult.paymentRequestId,
        });
        // Clear the cart after successful payment request
        clearCart();
        setFriendUsername("");
        setFriendMessage("");
        setPaymentRequestStatus("Request sent successfully!");
        onClose();
      } else {
        showErrorNotification(orderResult.message || "Failed to create order with friend payment.");
      }
    } catch (error) {
      console.error("Error processing friend payment:", error);
      showErrorNotification("Failed to process friend payment. Please try again.");
    } finally {
      setIsFriendPaymentLoading(false);
    }
  };



  // Calculate final amount after discount
  // The preview finalAmount includes only the food discount, so we add delivery fee
  const finalAmount = discount ? discount.finalAmount + deliveryFee : totalAmount;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <CurrencyConverterModal key="currency-converter" isOpen={showConverter} onClose={() => setShowConverter(false)} />
      <motion.div
        key="payment-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-accentwhite rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-primary-500 scrollbar-track-gray-100"
          role="dialog"
          aria-labelledby="payment-modal-title"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-pepperoni p-6 text-accentwhite relative sticky top-0 z-10">
            <h2 id="payment-modal-title" className="text-2xl font-bold font-display">Complete Your Payment</h2>
            <p className="text-accentwhite/90 text-sm mt-1 font-sans">
              {orderData ? `Order from ${restaurantName}` : "Loading order..."}
              <button
                onClick={() => setShowConverter(true)}
                className="ml-2 text-accentwhite/80 hover:text-accentwhite transition-colors"
              >
                <FaExchangeAlt size={14} />
              </button>
            </p>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="absolute top-4 right-4 text-accentwhite hover:text-gray-200"
              aria-label="Close modal"
            >
              <FaTimes size={20} />
            </motion.button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Order Summary (Collapsible) */}
            <div>
              <button
                onClick={() => setIsOrderSummaryOpen(!isOrderSummaryOpen)}
                className="w-full flex items-center justify-between p-3 bg-gray-100 rounded-xl font-semibold text-olive-dark font-display"
              >
                <span>Order Summary</span>
                {isOrderSummaryOpen ? <FaChevronUp /> : <FaChevronDown />}
              </button>
              <AnimatePresence>
                {isOrderSummaryOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    {orderData ? (
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 mt-2">
                        <h3 className="text-lg font-semibold text-olive-dark mb-3 font-display">
                          Order Summary
                        </h3>
                        <div className="space-y-2 text-sm font-sans">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Subtotal:</span>
                            <div className="text-right">
                              <span className="font-semibold">
                                {formatBytes(nairaToBytes(totalAmount - deliveryFee))}
                              </span>
                              <p className="text-xs text-gray-500">
                                ≈ {formatNaira(totalAmount - deliveryFee)}
                              </p>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Delivery Fee:</span>
                            <div className="text-right">
                              <span className="font-semibold">{formatBytes(nairaToBytes(deliveryFee))}</span>
                              <p className="text-xs text-gray-500">≈ {formatNaira(deliveryFee)}</p>
                            </div>
                          </div>
                          {discount && (
                            <div className="flex justify-between text-secondary-600">
                              <span>Discount ({discount.discountCode}):</span>
                              <div className="text-right">
                                <span>-{formatBytes(nairaToBytes(discount.savings))}</span>
                                <p className="text-xs text-gray-500">≈ -{formatNaira(discount.savings)}</p>
                              </div>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-base">
                            <span>Total:</span>
                            <div className="text-right">
                              <span className="text-pepperoni">{formatBytes(nairaToBytes(finalAmount))}</span>
                              <p className="text-xs text-gray-500">≈ {formatNaira(finalAmount)}</p>
                            </div>
                          </div>
                          {isExternal ? (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-gray-600">
                                <span className="font-semibold">Recipient:</span>{" "}
                                {recipientInfo.name}
                              </p>
                              <p className="text-gray-600">
                                <span className="font-semibold">Phone:</span>{" "}
                                {recipientInfo.phone}
                              </p>
                              <p className="text-gray-600">
                                <span className="font-semibold">Delivery to:</span>{" "}
                                {recipientInfo.location}
                              </p>
                            </div>
                          ) : (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-gray-600">
                                <span className="font-semibold">Recipient:</span> @
                                {recipientInfo.username}
                              </p>
                              <p className="text-gray-600">
                                <span className="font-semibold">Delivery to:</span>{" "}
                                {recipientInfo.location}
                              </p>
                              <p className="text-gray-600">
                                <span className="font-semibold">Phone:</span>{" "}
                                {recipientInfo.phone}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-gray-600 font-sans p-4">
                        Loading order details...
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Enhanced Discount Selector */}
            <EnhancedDiscountSelector
              orderAmount={totalAmount - deliveryFee}
              restaurantId={restaurantId}
              deliveryFee={deliveryFee}
              orderItems={orderData?.itemsForRestaurant || []}
              onDiscountApplied={(discountData) => {
                setDiscount(discountData);
                setDiscountError('');
              }}
              onDiscountRemoved={() => {
                setDiscount(null);
                setDiscountError('');
              }}
              disabled={loading || isWalletLoading || isFriendPaymentLoading}
            />

            {/* Payment Options */}
            <div className="space-y-4">
              {/* Wallet Payment */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleWalletPayment}
                disabled={loading || isWalletLoading || userBalance < finalAmount}
                className={`w-full p-4 border-2 rounded-xl transition-all flex items-center gap-4 font-sans ${loading || isWalletLoading || userBalance < finalAmount
                  ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                  : "border-secondary-200 hover:border-secondary-400 hover:bg-secondary-50"
                  }`}
              >
                {isWalletLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-secondary-600 border-t-transparent"></div>
                ) : (
                  <div className="bg-secondary-100 p-3 rounded-full">
                    <FaWallet className="text-secondary-600 text-xl" />
                  </div>
                )}
                <div className="flex-1 text-left">
                  <h3 className="font-bold text-olive-dark font-display">Pay from Wallet</h3>
                  <div className="flex flex-col">
                    <p className="text-sm text-gray-600">
                      Balance: {formatBytes(nairaToBytes(userBalance))}
                    </p>
                    <p className="text-xs text-gray-500">≈ {formatNaira(userBalance)}</p>
                  </div>
                </div>
                {userBalance >= finalAmount ? (
                  <div className="text-green-500 font-semibold font-sans">✓ Available</div>
                ) : (
                  <div className="text-primary-600 font-semibold text-xs font-sans">
                    Insufficient
                  </div>
                )}
              </motion.button>

              {/* Card Payment */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onCardPayment(orderData)}
                disabled={loading || isWalletLoading || isFriendPaymentLoading}
                className="w-full p-4 border-2 border-gray-200 rounded-xl bg-gray-50 opacity-60 cursor-not-allowed flex items-center gap-4 font-sans"
              >
                <div className="bg-gray-200 p-3 rounded-full">
                  <FaCreditCard className="text-gray-500 text-xl" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-bold text-gray-600 font-display">Pay with Card</h3>
                  <p className="text-sm text-gray-500 font-sans">
                    Coming Soon - We're working on it!
                  </p>
                </div>
                <div className="text-gray-400 font-semibold font-sans">🚧</div>
              </motion.button>

              {/* Friend Payment (Collapsible) */}
              <div>
                <button
                  onClick={() => setIsFriendPaymentOpen(!isFriendPaymentOpen)}
                  className="w-full flex items-center justify-between p-3 bg-primary-100 rounded-xl font-semibold text-olive-dark font-display"
                >
                  <span>Ask a Friend to Pay</span>
                  {isFriendPaymentOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                <AnimatePresence>
                  {isFriendPaymentOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="w-full p-4 border-2 border-primary-200 rounded-xl hover:border-primary-400 hover:bg-primary-50 transition-all space-y-3 mt-2">
                        <div className="flex items-center gap-4">
                          <div className="bg-primary-100 p-3 rounded-full">
                            <FaUserFriends className="text-primary-600 text-xl" />
                          </div>
                          <div className="flex-1 text-left">
                            <h3 className="font-bold text-olive-dark font-display">Ask a Friend to Pay</h3>
                            <p className="text-sm text-gray-600 font-sans">
                              Enter your friend's username
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={friendUsername}
                            onChange={(e) => setFriendUsername(e.target.value.replace("@", ""))}
                            placeholder="Enter friend's username"
                            className="w-full p-2 border border-primary-300 rounded-xl focus:ring-2 focus:ring-primary-400 focus:border-transparent font-sans"
                            disabled={isFriendPaymentLoading}
                          />
                          <textarea
                            value={friendMessage}
                            onChange={(e) => setFriendMessage(e.target.value)}
                            placeholder="Add a message (optional)"
                            maxLength={500}
                            className="w-full p-2 border border-primary-300 rounded-xl focus:ring-2 focus:ring-primary-400 focus:border-transparent font-sans"
                            rows={3}
                            disabled={isFriendPaymentLoading}
                          />
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleFriendPayment}
                            disabled={loading || isFriendPaymentLoading}
                            className={`w-full p-2 rounded-xl font-semibold font-sans flex items-center justify-center gap-2 ${loading || isFriendPaymentLoading
                              ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                              : "bg-primary-500 text-accentwhite hover:bg-primary-600"
                              }`}
                          >
                            {isFriendPaymentLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                Sending...
                              </>
                            ) : (
                              "Send Request"
                            )}
                          </motion.button>
                        </div>
                        {paymentRequestStatus && (
                          <p className="text-sm text-primary-600 mt-2 font-sans">
                            {paymentRequestStatus}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Cancel Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              disabled={loading || isWalletLoading || isFriendPaymentLoading}
              className="w-full p-3 text-gray-600 hover:text-olive-dark hover:bg-gray-100 font-semibold transition-all rounded-xl font-sans"
            >
              Cancel
            </motion.button>
          </div>

          {/* Loading Overlay */}
          {(loading || isWalletLoading || isFriendPaymentLoading) && (
            <div className="absolute inset-0 bg-accentwhite/80 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600 font-sans">Processing...</p>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EnhancedPaymentModal;